import React, { useState, useEffect } from 'react';
import { adminAPI } from '../services/api';
import axios from 'axios';
import { 
  Users, 
  CreditCard, 
  TrendingUp, 
  AlertCircle,
  Trash2,
  XCircle,
  CheckCircle,
  Euro,
  LifeBuoy,
  Clock,
  ChevronDown
} from 'lucide-react';

const STATUS_TICKET = {
  open:        { label: 'Aperto',      cls: 'badge-warning' },
  in_progress: { label: 'In gestione', cls: 'badge-info' },
  closed:      { label: 'Chiuso',      cls: 'badge-success' },
};

const Admin = () => {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [statsRes, usersRes, subsRes] = await Promise.all([
        adminAPI.getStats(),
        adminAPI.getUsers(),
        adminAPI.getSubscriptions()
      ]);
      setStats(statsRes.data.stats);
      setUsers(usersRes.data.users);
      setSubscriptions(subsRes.data.subscriptions);

      // Carica ticket
      const token = localStorage.getItem('token');
      const ticketsRes = await axios.get(
        `${process.env.REACT_APP_API_URL}/tickets/admin`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTickets(ticketsRes.data.tickets || []);
    } catch (error) {
      console.error('Errore caricamento dati admin:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTicketStatus = async (ticketId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/tickets/${ticketId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTickets(prev => prev.map(t => t.id === ticketId ? { ...t, status: newStatus } : t));
    } catch (error) {
      alert('Errore aggiornamento stato ticket');
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Sei sicuro di voler eliminare l'utente ${userName}? Questa azione è irreversibile.`)) return;
    try {
      await adminAPI.deleteUser(userId);
      alert('Utente eliminato con successo');
      loadData();
    } catch (error) {
      alert(error.response?.data?.error || "Errore durante l'eliminazione");
    }
  };

  const handleCancelSubscription = async (subId, userName) => {
    if (!window.confirm(`Sei sicuro di voler cancellare l'abbonamento di ${userName}?`)) return;
    try {
      await adminAPI.cancelSubscription(subId);
      alert('Abbonamento cancellato con successo');
      loadData();
    } catch (error) {
      alert(error.response?.data?.error || 'Errore durante la cancellazione');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      active:   { class: 'badge-success', text: 'Attivo',    icon: <CheckCircle size={14}/> },
      trialing: { class: 'badge-info',    text: 'Prova',     icon: <CheckCircle size={14}/> },
      past_due: { class: 'badge-warning', text: 'Scaduto',   icon: <AlertCircle size={14}/> },
      canceled: { class: 'badge-danger',  text: 'Cancellato',icon: <XCircle size={14}/> }
    };
    const b = badges[status] || { class: 'badge-info', text: status };
    return <span className={`badge ${b.class}`} style={{ display:'inline-flex', alignItems:'center', gap:'4px' }}>{b.icon}{b.text}</span>;
  };

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const openTickets = tickets.filter(t => t.status === 'open').length;

  if (loading) {
    return <div className="loading"><div className="spinner"></div><p>Caricamento pannello admin...</p></div>;
  }

  const tabs = [
    { id: 'overview',      label: 'Panoramica' },
    { id: 'users',         label: `Utenti (${users.length})` },
    { id: 'subscriptions', label: `Abbonamenti (${subscriptions.length})` },
    { id: 'tickets',       label: `Ticket (${openTickets} aperti)` },
  ];

  return (
    <div className="dashboard">
      <div className="container">
        <div className="dashboard-header">
          <h1>Pannello Amministratore</h1>
          <p style={{ color: 'var(--gray)', marginTop: '0.5rem' }}>Gestione utenti, abbonamenti e supporto</p>
        </div>

        {/* ── Tabs ── */}
        <div style={{ display:'flex', gap:'0.5rem', marginTop:'2rem', borderBottom:'2px solid var(--border)' }}>
          {tabs.map(t => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`btn ${activeTab === t.id ? 'btn-primary' : 'btn-outline'}`}
              style={{ borderRadius:'8px 8px 0 0', position:'relative' }}
            >
              {t.label}
              {t.id === 'tickets' && openTickets > 0 && (
                <span style={{
                  position:'absolute', top:'-6px', right:'-6px',
                  background:'var(--danger)', color:'white',
                  borderRadius:'99px', fontSize:'0.65rem',
                  padding:'1px 5px', fontWeight:700
                }}>{openTickets}</span>
              )}
            </button>
          ))}
        </div>

        {/* ── Panoramica ── */}
        {activeTab === 'overview' && stats && (
          <>
            <div className="dashboard-grid" style={{ marginTop:'2rem' }}>
              <div className="dashboard-card">
                <div className="card-header"><Users size={24} style={{ color:'var(--primary)' }}/><h3 className="card-title">Utenti Totali</h3></div>
                <div className="card-content"><p style={{ fontSize:'2.5rem', fontWeight:'bold', color:'var(--primary)' }}>{stats.totalUsers}</p></div>
              </div>
              <div className="dashboard-card">
                <div className="card-header"><CreditCard size={24} style={{ color:'var(--success)' }}/><h3 className="card-title">Abbonamenti Attivi</h3></div>
                <div className="card-content"><p style={{ fontSize:'2.5rem', fontWeight:'bold', color:'var(--success)' }}>{stats.activeSubscriptions}</p></div>
              </div>
              <div className="dashboard-card">
                <div className="card-header"><Euro size={24} style={{ color:'var(--secondary)' }}/><h3 className="card-title">Revenue Mensile</h3></div>
                <div className="card-content"><p style={{ fontSize:'2.5rem', fontWeight:'bold', color:'var(--secondary)' }}>€{stats.monthlyRevenue?.toFixed(2)}</p></div>
              </div>
              <div className="dashboard-card">
                <div className="card-header"><LifeBuoy size={24} style={{ color: openTickets > 0 ? 'var(--danger)' : 'var(--gray)' }}/><h3 className="card-title">Ticket Aperti</h3></div>
                <div className="card-content">
                  <p style={{ fontSize:'2.5rem', fontWeight:'bold', color: openTickets > 0 ? 'var(--danger)' : 'var(--gray)' }}>{openTickets}</p>
                  <p style={{ fontSize:'0.875rem', color:'var(--gray)' }}>su {tickets.length} totali</p>
                </div>
              </div>
            </div>

            {stats.subscriptionsByPlan?.length > 0 && (
              <div className="dashboard-card" style={{ marginTop:'2rem' }}>
                <div className="card-header"><h3 className="card-title"><TrendingUp size={20} style={{ marginRight:'0.5rem' }}/>Distribuzione Abbonamenti</h3></div>
                <div className="card-content">
                  {stats.subscriptionsByPlan.map((plan, idx) => (
                    <div key={idx} style={{ display:'flex', justifyContent:'space-between', padding:'1rem', background:'var(--light-gray)', borderRadius:'8px', marginBottom:'0.5rem' }}>
                      <span><strong>{plan.plan_name}</strong></span>
                      <span>{plan.count} abbonamenti (€{plan.price * plan.count}/mese)</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {stats.recentUsers?.length > 0 && (
              <div className="dashboard-card" style={{ marginTop:'2rem' }}>
                <div className="card-header"><h3 className="card-title">Ultimi Utenti Registrati</h3></div>
                <div className="card-content">
                  <div className="table-container">
                    <table className="table">
                      <thead><tr><th>Nome</th><th>Email</th><th>Data</th></tr></thead>
                      <tbody>
                        {stats.recentUsers.map(u => (
                          <tr key={u.id}><td>{u.name}</td><td>{u.email}</td><td>{formatDate(u.created_at)}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* ── Utenti ── */}
        {activeTab === 'users' && (
          <div className="table-container" style={{ marginTop:'2rem' }}>
            <table className="table">
              <thead>
                <tr><th>Nome</th><th>Email</th><th>Azienda</th><th>Piano</th><th>Stato</th><th>Registrato</th><th>Azioni</th></tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id}>
                    <td><strong>{u.name}</strong></td>
                    <td>{u.email}</td>
                    <td>{u.company || '-'}</td>
                    <td>{u.plan_name || 'Nessuno'}</td>
                    <td>{u.status ? getStatusBadge(u.status) : '-'}</td>
                    <td>{formatDate(u.created_at)}</td>
                    <td>
                      <button onClick={() => handleDeleteUser(u.id, u.name)} className="btn btn-danger" style={{ padding:'0.5rem 1rem', fontSize:'0.875rem' }} disabled={u.role === 'admin'}>
                        <Trash2 size={16}/>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ── Abbonamenti ── */}
        {activeTab === 'subscriptions' && (
          <div className="table-container" style={{ marginTop:'2rem' }}>
            <table className="table">
              <thead>
                <tr><th>Utente</th><th>Email</th><th>Piano</th><th>Stato</th><th>Inizio</th><th>Rinnovo</th><th>Azioni</th></tr>
              </thead>
              <tbody>
                {subscriptions.map(s => (
                  <tr key={s.id}>
                    <td><strong>{s.name}</strong></td>
                    <td>{s.email}</td>
                    <td>{s.plan_name}</td>
                    <td>{getStatusBadge(s.status)}</td>
                    <td>{formatDate(s.current_period_start)}</td>
                    <td>
                      {formatDate(s.current_period_end)}
                      {s.cancel_at_period_end && <span className="badge badge-warning" style={{ marginLeft:'0.5rem', fontSize:'0.75rem' }}>In cancellazione</span>}
                    </td>
                    <td>
                      {s.status === 'active' && !s.cancel_at_period_end && (
                        <button onClick={() => handleCancelSubscription(s.id, s.name)} className="btn btn-danger" style={{ padding:'0.5rem 1rem', fontSize:'0.875rem' }}>
                          <XCircle size={16}/> Cancella
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ── Ticket ── */}
        {activeTab === 'tickets' && (
          <div style={{ marginTop:'2rem' }}>
            {tickets.length === 0 ? (
              <div className="dashboard-card">
                <div className="card-content" style={{ textAlign:'center', padding:'2rem', color:'var(--gray)' }}>
                  <LifeBuoy size={40} style={{ marginBottom:'1rem', opacity:0.4 }}/>
                  <p>Nessun ticket ricevuto.</p>
                </div>
              </div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:'1rem' }}>
                {tickets.map(t => {
                  const s = STATUS_TICKET[t.status] || { label: t.status, cls: 'badge-info' };
                  return (
                    <div key={t.id} className="dashboard-card">
                      <div className="card-content">
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:'0.75rem' }}>
                          <div>
                            <div style={{ display:'flex', alignItems:'center', gap:'0.75rem', marginBottom:'0.4rem' }}>
                              <span style={{ fontWeight:700, fontSize:'0.8rem', color:'var(--gray)' }}>#{t.id}</span>
                              <span className={`badge ${s.cls}`}>{s.label}</span>
                              {t.agent_name && <span style={{ fontSize:'0.8rem', color:'var(--gray)' }}>Agente: {t.agent_name}</span>}
                            </div>
                            <p style={{ fontWeight:600, marginBottom:'0.25rem' }}>{t.subject}</p>
                            <p style={{ fontSize:'0.875rem', color:'var(--gray)', whiteSpace:'pre-wrap' }}>{t.body}</p>
                            <p style={{ fontSize:'0.8rem', color:'var(--gray)', marginTop:'0.5rem', display:'flex', alignItems:'center', gap:'4px' }}>
                              <Clock size={12}/> {formatDate(t.created_at)} — {t.user_name} ({t.user_email})
                            </p>
                          </div>
                          <div style={{ display:'flex', alignItems:'center', gap:'0.5rem' }}>
                            <select
                              value={t.status}
                              onChange={e => handleUpdateTicketStatus(t.id, e.target.value)}
                              style={{ padding:'0.4rem 0.75rem', borderRadius:'6px', border:'1px solid var(--light-gray)', fontSize:'0.8rem', cursor:'pointer' }}
                            >
                              <option value="open">Aperto</option>
                              <option value="in_progress">In gestione</option>
                              <option value="closed">Chiuso</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default Admin;
